import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from './apiService';
import { ApiResponse } from '../types/api';
import { invalidateBalanceQueries } from './moneyService';

export interface PlaceBtcgraphBetRequest {
  gameId: string;
  roundStart: number;
  predictionId: string;
  amount: number;
}

export interface PlaceBtcgraphBetResponse {
  status: number;
  message?: { warning?: string };
  balance?: number;
  betId?: string;
}

// Minigame Services
export async function getMinigameList(params: any): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/minigame/getGameList', params);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getMinigameBettingHistory(params: any): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/minigame/getBettingHistory', params);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteMinigameBettingHistory(data: any): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/minigame/deleteBettingHistory', data);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function placeBtcgraphBet(params: PlaceBtcgraphBetRequest): Promise<PlaceBtcgraphBetResponse> {
  const response = await apiService.postRaw<PlaceBtcgraphBetResponse>('/api/minigame/placeBet', params);
  if (response?.status !== 0) {
    throw new Error(response?.message?.warning || 'Failed to place bet');
  }
  return response;
}

export interface SettleRoundRequest {
  changePct: number;
}

export interface SettleRoundResponse {
  status: number;
  settled?: number;
  roundId?: string;
  winAmount?: number;
  betAmount?: number;
}

export async function settleRound(params: SettleRoundRequest): Promise<SettleRoundResponse> {
  const response = await apiService.postRaw<SettleRoundResponse>('/api/minigame/settleRound', params);
  return response;
}

/** Backend btcgraph state: past ~1min price data + round info for chart */
export interface BtcgraphStateResponse {
  serverTime?: number;
  phase?: string;
  roundStartAt?: number;
  startPrice?: number;
  lastPrice?: number;
  priceHistory?: { time: number; price: number }[];
  config?: { roundCycleMs?: number; settledDisplayMs?: number };
}

export async function getBtcgraphState(): Promise<BtcgraphStateResponse> {
  const response = await apiService.getRaw<BtcgraphStateResponse>('/api/minigame/btcgraph/state');
  return response;
}

// GameMonetize (iframe HTML5 games from feed)
export interface GameMonetizeGame {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  thumb: string | null;
  game_url: string;
  category: string | null;
  created_at?: string;
}

export async function getGameMonetizeGames(params?: { category?: string; limit?: number; offset?: number }): Promise<{ status: number; data: GameMonetizeGame[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.category) q.set('category', params.category);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const response = await apiService.getRaw<{ status: number; data: GameMonetizeGame[]; total: number }>(`/api/gamemonetize/games?${q.toString()}`);
  return response;
}

export async function getGameMonetizeCategories(): Promise<{ status: number; data: string[] }> {
  const response = await apiService.getRaw<{ status: number; data: string[] }>('/api/gamemonetize/games/categories');
  return response;
}

export async function getGameMonetizeBySlug(slug: string): Promise<{ status: number; data: GameMonetizeGame | null }> {
  const response = await apiService.getRaw<{ status: number; data: GameMonetizeGame | null }>(`/api/gamemonetize/games/slug/${encodeURIComponent(slug)}`);
  return response;
}

// React Query Hooks
export function useMinigameList(params: any) {
  return useQuery({
    queryKey: ['minigame', 'list', params],
    queryFn: () => getMinigameList(params),
    enabled: apiService.isAuthenticated(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMinigameBettingHistory(params: any) {
  return useQuery({
    queryKey: ['minigame', 'bettingHistory', params],
    queryFn: () => getMinigameBettingHistory(params),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useDeleteMinigameBettingHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMinigameBettingHistory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['minigame', 'bettingHistory'] });
    },
  });
}

export function usePlaceBtcgraphBet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: placeBtcgraphBet,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['minigame', 'bettingHistory'] });
    },
  });
}
